const S = {
  wrapper:      { marginTop:'10px', padding:'40px', border:'3px solid grey', fontFamily:"'Georgia', serif", color:'#1a1a2e', lineHeight:'1.7' },
  h1:           { fontSize:'2rem', fontWeight:'700', borderBottom:'3px solid #c0392b', paddingBottom:'8px', marginBottom:'24px', color:'#7B0D0D' },
  h2:           { fontSize:'1.3rem', fontWeight:'700', color:'#c0392b', marginTop:'36px', marginBottom:'12px', borderLeft:'4px solid #c0392b', paddingLeft:'10px' },
  p:            { marginBottom:'12px', fontSize:'0.97rem' },
  infoBox:      { background:'#FEF0F0', border:'2px solid #c0392b', borderRadius:'6px', padding:'16px 20px', margin:'16px 0' },
  infoBoxTitle: { fontWeight:'700', fontFamily:"'Courier New', monospace", fontSize:'0.88rem', color:'#7B0D0D', marginBottom:'8px' },
  infoBoxItem:  { fontSize:'0.9rem', margin:'4px 0', paddingLeft:'12px', borderLeft:'2px solid #c0392b' },
  table:        { width:'100%', borderCollapse:'collapse', marginBottom:'20px', fontSize:'0.92rem' },
  th:           { background:'#c0392b', color:'#fff', padding:'10px 14px', textAlign:'left', fontWeight:'700', fontSize:'0.9rem' },
  tdE:          { padding:'10px 14px', borderBottom:'1px solid #f0cece', verticalAlign:'top', background:'#fff5f5' },
  tdO:          { padding:'10px 14px', borderBottom:'1px solid #f0cece', verticalAlign:'top', background:'#fff' },
  tdL:          { fontWeight:'600', color:'#7B0D0D', whiteSpace:'nowrap', width:'220px' },
  code:         { fontFamily:"'Courier New', monospace", fontSize:'0.88rem', whiteSpace:'pre-wrap' },
  dWrap:        { margin:'24px 0', background:'#fffafa', border:'1px solid #f0cece', borderRadius:'8px', padding:'20px' },
  dTitle:       { fontWeight:'700', fontSize:'0.95rem', color:'#7B0D0D', marginBottom:'12px', textAlign:'center', letterSpacing:'0.02em' },
  dCaption:     { fontSize:'0.84rem', color:'#555', textAlign:'center', marginTop:'10px', fontStyle:'italic' },
};

function InfoBox({ title, items }) {
  return (
    <div style={S.infoBox}>
      <div style={S.infoBoxTitle}>{title}</div>
      {items.map((item, i) => <div key={i} style={S.infoBoxItem}>{item}</div>)}
    </div>
  );
}

function Table2({ rows }) {
  return (
    <table style={S.table}>
      <thead><tr>
        <th style={{ ...S.th, width:'220px' }}>Concept</th>
        <th style={S.th}>Explanation / Formula</th>
      </tr></thead>
      <tbody>
        {rows.map(([label, content], i) => (
          <tr key={i}>
            <td style={{ ...(i%2===0?S.tdE:S.tdO), ...S.tdL }}>{label}</td>
            <td style={{ ...(i%2===0?S.tdE:S.tdO), ...S.code }}>{content}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

// ── Arrow marker defs ─────────────────────────────────────────────
function Arr() {
  return (
    <defs>
      <marker id="mRed"  markerWidth="8" markerHeight="8" refX="6" refY="4" orient="auto"><polygon points="0 0,7 4,0 8" fill="#c0392b"/></marker>
      <marker id="mBlue" markerWidth="8" markerHeight="8" refX="6" refY="4" orient="auto"><polygon points="0 0,7 4,0 8" fill="#2E75B6"/></marker>
      <marker id="mGrn"  markerWidth="8" markerHeight="8" refX="6" refY="4" orient="auto"><polygon points="0 0,7 4,0 8" fill="#27ae60"/></marker>
      <marker id="mOrg"  markerWidth="8" markerHeight="8" refX="6" refY="4" orient="auto"><polygon points="0 0,7 4,0 8" fill="#e07b00"/></marker>
      <marker id="mGry"  markerWidth="8" markerHeight="8" refX="6" refY="4" orient="auto"><polygon points="0 0,7 4,0 8" fill="#555"/></marker>
    </defs>
  );
}

// ─────────────────────────────────────────────────────────────────
// Diagram 1: Physical Meaning — energy picture at resonance
// ─────────────────────────────────────────────────────────────────
function DiagPhysicalMeaning() {
  const W = 900, H = 380;

  // Spring coil helper
  const coil = (x0, x1, y, n=7) => {
    const cw = (x1-x0)/n, pts = [[x0,y]];
    for (let i=0;i<n;i++) { pts.push([x0+i*cw+cw*0.25,y-14]); pts.push([x0+i*cw+cw*0.75,y+14]); }
    pts.push([x1,y]);
    return pts.map((p,i)=>`${i===0?'M':'L'}${p[0].toFixed(1)},${p[1].toFixed(1)}`).join(' ');
  };

  // Computed resonance growth: x(t)=t·sin(2πt)/(4π), showing 3 cycles
  const ωn = 2*Math.PI, pts = [];
  for (let t=0; t<=3; t+=0.01) pts.push([t, (ωn*t/2)*Math.sin(ωn*t)/ωn]);

  const tx = t => 490 + t*120;
  const ty = y => 200 - y*55;

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Physical Meaning of Resonance — Energy Input Every Cycle</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{width:'100%',display:'block'}}>
        <Arr/>

        {/* ── LEFT: physical system at resonance (3 phases) ── */}
        <rect x={10} y={10} width={460} height={H-20} rx={5} fill="#fff" stroke="#f0cece"/>
        <text x={235} y={30} textAnchor="middle" fontSize="14" fill="#7B0D0D" fontWeight="700">Why Resonance Grows: Force Always Does Positive Work</text>

        {/* Three snapshots stacked */}
        {[
          { yOff:55,  label:'t = 0  (start)', xPos:220, vDir:0,  fDir:1,  desc:'Mass at equilibrium, moving right. Force pushes right → positive work.' },
          { yOff:155, label:'t = T/4',        xPos:310, vDir:1,  fDir:0,  desc:'Mass at max displacement, velocity = 0. Force now = 0 (cos). Turning point.' },
          { yOff:255, label:'t = T/2',        xPos:220, vDir:-1, fDir:-1, desc:'Mass moving left, force also left → positive work again.' },
        ].map(({yOff, label, xPos, vDir, fDir, desc}, i) => {
          const wallX = 30;
          return (
            <g key={i}>
              {/* Ground line */}
              <line x1={wallX} y1={yOff+68} x2={440} y2={yOff+68} stroke="#ddd" strokeWidth="1"/>
              {/* Wall */}
              <rect x={wallX} y={yOff+15} width={10} height={55} fill="#888"/>
              {[yOff+15,yOff+29,yOff+43,yOff+57].map(y=>(<line key={y} x1={wallX} y1={y} x2={wallX-10} y2={y+12} stroke="#888" strokeWidth="1.5"/>))}
              {/* Spring */}
              <path d={coil(wallX+10, xPos-28, yOff+42)} fill="none" stroke="#27ae60" strokeWidth="2"/>
              {/* Mass */}
              <rect x={xPos-28} y={yOff+20} width={56} height={44} rx={4} fill="#2E75B6" stroke="#1a5276" strokeWidth="2"/>
              <text x={xPos} y={yOff+46} textAnchor="middle" fontSize="14" fill="#fff" fontWeight="700">m</text>
              {/* velocity arrow */}
              {vDir !== 0 && (
                <line x1={xPos+28} y1={yOff+42} x2={xPos+28+vDir*40} y2={yOff+42}
                  stroke="#2E75B6" strokeWidth="2.5" markerEnd="url(#mBlue)"/>
              )}
              {vDir !== 0 && <text x={xPos+28+vDir*50} y={yOff+38} fontSize="11" fill="#2E75B6" fontWeight="700">v</text>}
              {/* force arrow */}
              {fDir !== 0 && (
                <line x1={xPos+28} y1={yOff+54} x2={xPos+28+fDir*40} y2={yOff+54}
                  stroke="#c0392b" strokeWidth="2.5" markerEnd="url(#mRed)"/>
              )}
              {fDir !== 0 && <text x={xPos+28+fDir*50} y={yOff+68} fontSize="11" fill="#c0392b" fontWeight="700">F</text>}
              {/* label */}
              <text x={240} y={yOff+12} fontSize="11" fill="#7B0D0D" fontWeight="700">{label}</text>
              <text x={240} y={yOff+80} fontSize="10" fill="#555">{desc}</text>
            </g>
          );
        })}

        {/* Work done annotation */}
        <rect x={16} y={338} width={448} height={30} rx={3} fill="#FEF0F0" stroke="#c0392b" strokeWidth="1.5"/>
        <text x={240} y={350} textAnchor="middle" fontSize="11" fill="#7B0D0D" fontWeight="700">At resonance: F and v always in phase → W = F·v·dt always positive → energy grows every cycle</text>
        <text x={240} y={363} textAnchor="middle" fontSize="11" fill="#555">Off resonance: F and v drift in/out of phase → net work per cycle = 0 → no growth</text>

        {/* ── RIGHT: resonance growth waveform ── */}
        <rect x={478} y={10} width={412} height={H-20} rx={5} fill="#fff" stroke="#f0cece"/>
        <text x={684} y={28} textAnchor="middle" fontSize="13" fill="#7B0D0D" fontWeight="700">Undamped Resonance: x(t) grows without bound</text>

        {/* Graph area */}
        <rect x={490} y={38} width={390} height={270} rx={3} fill="#fafafa" stroke="#ddd"/>

        {/* Grid */}
        {[-2,-1,0,1,2].map(v => (
          <g key={v}>
            <line x1={490} y1={ty(v)} x2={882} y2={ty(v)} stroke={v===0?'#ccc':'#ebebeb'} strokeWidth={v===0?1.5:0.8}/>
            <text x={485} y={ty(v)+4} textAnchor="end" fontSize="10" fill="#888">{v}</text>
          </g>
        ))}
        {[0,1,2,3].map(t => (
          <g key={t}>
            <line x1={tx(t)} y1={38} x2={tx(t)} y2={308} stroke="#ebebeb" strokeWidth="0.8"/>
            <text x={tx(t)} y={322} textAnchor="middle" fontSize="10" fill="#888">{t}s</text>
          </g>
        ))}

        {/* Envelope */}
        {[1,-1].map(s => {
          const ep = [];
          for (let t=0; t<=3; t+=0.05) ep.push([t, s*t/2]);
          return <path key={s} d={ep.map(([t,y],i)=>`${i===0?'M':'L'}${tx(t).toFixed(1)},${ty(y).toFixed(1)}`).join(' ')} fill="none" stroke="#e07b00" strokeWidth="1.8" strokeDasharray="7,4" opacity="0.8"/>;
        })}
        <text x={tx(2.6)} y={ty(1.3)+4} fontSize="11" fill="#e07b00" fontWeight="700">envelope: A(t) = F\u2080t/(2m\u03C9\u2099)</text>

        {/* Resonance curve */}
        <path d={pts.map(([t,y],i)=>`${i===0?'M':'L'}${tx(t).toFixed(1)},${ty(y).toFixed(1)}`).join(' ')} fill="none" stroke="#c0392b" strokeWidth="2.5"/>

        {/* Axes */}
        <line x1={488} y1={38} x2={488} y2={312} stroke="#555" strokeWidth="1.5" markerEnd="url(#mGry)"/>
        <line x1={486} y1={ty(0)} x2={885} y2={ty(0)} stroke="#555" strokeWidth="1.5" markerEnd="url(#mGry)"/>
        <text x={484} y={34} textAnchor="end" fontSize="11" fill="#555">x(t)</text>
        <text x={888} y={ty(0)+4} fontSize="11" fill="#555">t</text>

        {/* Key formula */}
        <rect x={490} y={318} width={390} height={42} rx={3} fill="#FEF0F0" stroke="#c0392b" strokeWidth="1.5"/>
        <text x={685} y={334} textAnchor="middle" fontSize="12" fill="#7B0D0D" fontWeight="700">x(t) = (F\u2080 / 2m\u03C9\u2099) \u00B7 t \u00B7 sin(\u03C9\u2099 t)</text>
        <text x={685} y={352} textAnchor="middle" fontSize="11" fill="#555">Amplitude grows linearly with time — no steady state exists</text>
      </svg>
      <div style={S.dCaption}>
        Resonance occurs when the forcing frequency matches the natural frequency. At this condition the applied force is always in phase with the velocity — it always does positive work on the system, adding energy every cycle. With no damping, energy accumulates indefinitely and amplitude grows linearly with time. Even tiny forces eventually produce catastrophic amplitudes.
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// Diagram 2: Undamped vs Damped — Side-by-side FRF comparison
// ─────────────────────────────────────────────────────────────────
function DiagUndampedVsDamped() {
  const W = 900, H = 420;
  const nPts = 500, rMax = 2.5;
  const ωn = 1;

  // Undamped: D = 1/(1-r²), split at r=1
  const undampedSegs = [[], []]; let seg = 0;
  for (let i=0; i<=nPts; i++) {
    const r = i/nPts*rMax;
    if (Math.abs(1-r*r) < 0.018) { seg=1; continue; }
    undampedSegs[seg].push([r, 1/(1-r*r)]);
  }

  // Damped for several zeta
  const zetaCurves = [
    { z:0.05, c:'#9b59b6', lbl:'ζ=0.05' },
    { z:0.15, c:'#c0392b', lbl:'ζ=0.15' },
    { z:0.3,  c:'#e07b00', lbl:'ζ=0.30' },
    { z:0.7,  c:'#27ae60', lbl:'ζ=0.70' },
  ];
  const Dd = (r,z) => 1/Math.sqrt((1-r*r)**2+(2*z*r)**2);
  const Ph = (r,z) => { let p=Math.atan2(2*z*r,1-r*r); return p<0?p+Math.PI:p; };

  // Amplitude panel
  const axX=50, axY=30, gW=390, gH=220;
  const mx = r => axX + (r/rMax)*gW;
  const myA = D => axY + (1-Math.min(D,8)/8)*gH;

  // Phase panel (same x scale, below)
  const phY = 278;
  const myP = p => phY + (p/Math.PI)*90;

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Undamped vs Damped Resonance — Amplitude &amp; Phase Comparison</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{width:'100%',display:'block'}}>
        <Arr/>

        {/* ── AMPLITUDE PLOT ── */}
        <rect x={axX} y={axY} width={gW} height={gH} rx={3} fill="#fafafa" stroke="#ddd"/>

        {/* Grid lines */}
        {[0,1,2,4,6,8].map(v => (
          <g key={v}>
            <line x1={axX} y1={myA(v)} x2={axX+gW} y2={myA(v)} stroke={v===1?'#bbb':'#ebebeb'} strokeWidth={v===1?1.5:0.8} strokeDasharray={v===1?'6,3':''}/>
            <text x={axX-5} y={myA(v)+4} textAnchor="end" fontSize="10" fill="#888">{v}</text>
          </g>
        ))}
        {[0,0.5,1,1.5,2,2.5].map(r => (
          <g key={r}>
            <line x1={mx(r)} y1={axY} x2={mx(r)} y2={axY+gH} stroke={r===1?'#c0392b':'#ebebeb'} strokeWidth={r===1?1.5:0.7} strokeDasharray={r===1?'6,3':''}/>
            <text x={mx(r)} y={axY+gH+13} textAnchor="middle" fontSize="10" fill={r===1?'#c0392b':'#888'}>{r}</text>
          </g>
        ))}
        <text x={mx(1)} y={axY+10} textAnchor="middle" fontSize="10" fill="#c0392b" fontWeight="700">r = 1 (resonance)</text>

        {/* D=1 reference */}
        <line x1={axX} y1={myA(1)} x2={axX+gW} y2={myA(1)} stroke="#aaa" strokeWidth="1.2" strokeDasharray="5,3"/>
        <text x={axX+gW+4} y={myA(1)+4} fontSize="10" fill="#aaa">D=1</text>

        {/* Undamped curves */}
        {undampedSegs.map((seg2, si) => {
          if (!seg2.length) return null;
          const positive = seg2[0][1] > 0;
          return <path key={si} d={seg2.map(([r,D],i)=>`${i===0?'M':'L'}${mx(r).toFixed(1)},${myA(D).toFixed(1)}`).join(' ')} fill="none" stroke={positive?'#1a1a2e':'#2E75B6'} strokeWidth="2" strokeDasharray="8,4"/>;
        })}

        {/* Damped curves */}
        {zetaCurves.map(({z,c}) => (
          <path key={z}
            d={Array.from({length:nPts+1},(_,i)=>{const r=i/nPts*rMax; return `${i===0?'M':'L'}${mx(r).toFixed(1)},${myA(Dd(r,z)).toFixed(1)}`;}).join(' ')}
            fill="none" stroke={c} strokeWidth="2.2"/>
        ))}

        {/* Axis labels */}
        <line x1={axX-2} y1={axY} x2={axX-2} y2={axY+gH+4} stroke="#555" strokeWidth="1.5"/>
        <text x={18} y={axY+gH/2} textAnchor="middle" fontSize="11" fill="#555" transform={`rotate(-90,18,${axY+gH/2})`}>Amplitude D(r)</text>
        <text x={axX} y={axY-6} fontSize="12" fill="#7B0D0D" fontWeight="700">Amplitude Ratio D(r)</text>

        {/* ── PHASE PLOT ── */}
        <rect x={axX} y={phY} width={gW} height={90} rx={3} fill="#fafafa" stroke="#ddd"/>
        {[0,90,180].map(deg => (
          <g key={deg}>
            <line x1={axX} y1={myP(deg*Math.PI/180)} x2={axX+gW} y2={myP(deg*Math.PI/180)} stroke={deg===90?'#bbb':'#ebebeb'} strokeWidth={deg===90?1.5:0.8} strokeDasharray={deg===90?'5,3':''}/>
            <text x={axX-5} y={myP(deg*Math.PI/180)+4} textAnchor="end" fontSize="10" fill="#888">{deg}°</text>
          </g>
        ))}
        {[0,0.5,1,1.5,2,2.5].map(r => (
          <g key={r}>
            <line x1={mx(r)} y1={phY} x2={mx(r)} y2={phY+90} stroke={r===1?'#c0392b':'#ebebeb'} strokeWidth={r===1?1.5:0.7} strokeDasharray={r===1?'6,3':''}/>
            <text x={mx(r)} y={phY+104} textAnchor="middle" fontSize="10" fill="#888">{r}</text>
          </g>
        ))}
        {/* Undamped phase: step function */}
        <path d={`M ${mx(0)},${myP(0)} L ${mx(0.98)},${myP(0)} L ${mx(1.02)},${myP(Math.PI)} L ${mx(rMax)},${myP(Math.PI)}`} fill="none" stroke="#1a1a2e" strokeWidth="2" strokeDasharray="8,4"/>
        {/* Damped phase curves */}
        {zetaCurves.map(({z,c}) => (
          <path key={z}
            d={Array.from({length:nPts+1},(_,i)=>{const r=i/nPts*rMax; return `${i===0?'M':'L'}${mx(r).toFixed(1)},${myP(Ph(r,z)).toFixed(1)}`;}).join(' ')}
            fill="none" stroke={c} strokeWidth="2.2"/>
        ))}
        {/* 90° at r=1 universal marker */}
        <circle cx={mx(1)} cy={myP(Math.PI/2)} r={5} fill="#c0392b" stroke="#fff" strokeWidth="1.5"/>
        <text x={mx(1)+8} y={myP(Math.PI/2)-4} fontSize="10" fill="#c0392b" fontWeight="700">90° for ALL ζ</text>

        <line x1={axX-2} y1={phY} x2={axX-2} y2={phY+92} stroke="#555" strokeWidth="1.5"/>
        <text x={18} y={phY+46} textAnchor="middle" fontSize="11" fill="#555" transform={`rotate(-90,18,${phY+46})`}>Phase φ</text>
        <text x={axX+gW/2} y={phY+118} textAnchor="middle" fontSize="11" fill="#555">Frequency ratio  r = ω / ωₙ</text>

        {/* ── RIGHT: comparison summary ── */}
        <rect x={460} y={10} width={430} height={H-20} rx={5} fill="#fff" stroke="#f0cece"/>
        <text x={675} y={30} textAnchor="middle" fontSize="14" fill="#7B0D0D" fontWeight="700">Key Differences</text>

        {/* Legend */}
        <line x1={468} y1={46} x2={500} y2={46} stroke="#1a1a2e" strokeWidth="2.5" strokeDasharray="8,4"/>
        <text x={504} y={50} fontSize="12" fill="#1a1a2e" fontWeight="700">Undamped (ζ=0)</text>
        {zetaCurves.map(({z,c,lbl},i) => (
          <g key={i}>
            <line x1={468} y1={66+i*18} x2={500} y2={66+i*18} stroke={c} strokeWidth="2.5"/>
            <text x={504} y={70+i*18} fontSize="12" fill={c} fontWeight="700">{lbl}</text>
          </g>
        ))}

        {/* Comparison panels */}
        {[
          { y:146, bg:'#1a1a2e', lbl:'Undamped  (ζ = 0)', tc:'#fff',
            pts:['Amplitude \u2192 \u221E exactly at r = 1','No steady state at resonance','Phase: 0\u00B0 for r<1, jumps to 180\u00B0 at r=1','FRF has vertical asymptote'] },
          { y:254, bg:'#FEF0F0', lbl:'Damped  (ζ > 0)', tc:'#7B0D0D',
            pts:['Peak finite: D\u2098\u2090\u2093 = 1/(2\u03B6\u221A(1\u2212\u03B6\u00B2))','Steady state exists (after transient dies)','Phase: smooth transition 0\u00B0 \u2192 90\u00B0 \u2192 180\u00B0','90\u00B0 at r=1 for ALL values of \u03B6'] },
          { y:362, bg:'#eaf3ff', lbl:'Common to both', tc:'#2E75B6',
            pts:['Natural frequency location: r = 1','\u03C9\u2099 = \u221A(k/m) regardless of \u03B6','90\u00B0 phase crossing identifies \u03C9\u2099','D = 1 in static limit (r\u21920)'] },
        ].map(({y,bg,lbl,tc,pts}) => (
          <g key={y}>
            <rect x={466} y={y} width={418} height={102} rx={4} fill={bg} opacity={bg==='#1a1a2e'?1:1} stroke={bg==='#1a1a2e'?'#555':'#ddd'}/>
            <text x={472} y={y+18} fontSize="13" fill={tc} fontWeight="700">{lbl}</text>
            {pts.map((pt,i) => (
              <g key={i}>
                <circle cx={476} cy={y+32+i*18} r={3} fill={tc === '#fff' ? '#c0392b' : '#c0392b'}/>
                <text x={484} y={y+36+i*18} fontSize="11" fill={bg==='#1a1a2e'?'#eee':'#444'}>{pt}</text>
              </g>
            ))}
          </g>
        ))}
      </svg>
      <div style={S.dCaption}>
        Top: amplitude ratio D(r). Dashed lines show undamped response — vertical asymptote at r=1 for r&lt;1 (in-phase, black) and r&gt;1 (out-of-phase, blue). Solid coloured curves show damped responses: the asymptote is replaced by a finite peak that shortens and broadens as ζ increases. Bottom: phase angle. The undamped phase jumps discontinuously at r=1; damped systems pass smoothly through 90° there. The 90° crossing is universal — it identifies ωₙ regardless of damping.
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// Diagram 3: Peak amplitude & resonance bandwidth anatomy
// ─────────────────────────────────────────────────────────────────
function DiagPeakAnatomy() {
  const W = 900, H = 360;
  const z = 0.12, rMax = 2.0, nPts = 600;
  const Dd = (r,z) => 1/Math.sqrt((1-r*r)**2+(2*z*r)**2);
  const rPeak = Math.sqrt(1-2*z*z);
  const Dpeak = Dd(rPeak, z);

  const gX=55, gY=25, gW=530, gH=270;
  const mx = r => gX + (r/rMax)*gW;
  const myD = D => gY + (1 - Math.min(D,8)/8)*gH;
  const Dhp = Dpeak/Math.sqrt(2);

  // Find half-power frequencies numerically
  let r1=0, r2=0;
  for (let i=1; i<=nPts; i++) {
    const ra=(i-1)/nPts*rMax, rb=i/nPts*rMax;
    if (Dd(ra,z)<Dhp && Dd(rb,z)>=Dhp && ra<1) r1=rb;
    if (Dd(ra,z)>=Dhp && Dd(rb,z)<Dhp && ra>1) r2=ra;
  }

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Resonance Peak Anatomy — Key Quantities &amp; Half-Power Bandwidth</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{width:'100%',display:'block'}}>
        <Arr/>
        <rect x={gX} y={gY} width={gW} height={gH} rx={3} fill="#fafafa" stroke="#ddd"/>

        {/* Grid */}
        {[0,1,2,3,4,5,6,7,8].map(v => (
          <g key={v}>
            <line x1={gX} y1={myD(v)} x2={gX+gW} y2={myD(v)} stroke={v===1?'#ccc':'#ebebeb'} strokeWidth={v===1?1.5:0.7} strokeDasharray={v===1?'6,3':''}/>
            <text x={gX-6} y={myD(v)+4} textAnchor="end" fontSize="10" fill="#888">{v}</text>
          </g>
        ))}
        {[0,0.5,1.0,1.5,2.0].map(r => (
          <g key={r}>
            <line x1={mx(r)} y1={gY} x2={mx(r)} y2={gY+gH} stroke="#ebebeb" strokeWidth="0.7"/>
            <text x={mx(r)} y={gY+gH+13} textAnchor="middle" fontSize="10" fill="#888">{r}</text>
          </g>
        ))}

        {/* Shaded area under peak */}
        {(() => {
          const fillPts = [];
          for (let i=0; i<=nPts; i++) { const r=i/nPts*rMax; fillPts.push([r,Dd(r,z)]); }
          const d = fillPts.map(([r,D],i)=>`${i===0?'M':'L'}${mx(r).toFixed(1)},${myD(D).toFixed(1)}`).join(' ')
            + ` L ${mx(rMax)},${myD(0)} L ${mx(0)},${myD(0)} Z`;
          return <path d={d} fill="#c0392b" opacity="0.06"/>;
        })()}

        {/* Main curve */}
        <path
          d={Array.from({length:nPts+1},(_,i)=>{const r=i/nPts*rMax; return `${i===0?'M':'L'}${mx(r).toFixed(1)},${myD(Dd(r,z)).toFixed(1)}`;}).join(' ')}
          fill="none" stroke="#c0392b" strokeWidth="3"/>

        {/* ① D=1 static line */}
        <line x1={gX} y1={myD(1)} x2={gX+gW} y2={myD(1)} stroke="#2E75B6" strokeWidth="1.5" strokeDasharray="6,3"/>
        <text x={gX+gW+5} y={myD(1)+4} fontSize="11" fill="#2E75B6" fontWeight="700">① D=1</text>

        {/* ② Peak point */}
        <circle cx={mx(rPeak)} cy={myD(Dpeak)} r={7} fill="#c0392b" stroke="#fff" strokeWidth="2"/>
        <line x1={mx(rPeak)} y1={myD(Dpeak)} x2={mx(rPeak)} y2={gY+gH} stroke="#c0392b" strokeWidth="1.2" strokeDasharray="4,3"/>
        <text x={mx(rPeak)} y={gY+gH+26} textAnchor="middle" fontSize="10" fill="#c0392b" fontWeight="700">r*={rPeak.toFixed(3)}</text>
        {/* Peak label callout */}
        <line x1={mx(rPeak)} y1={myD(Dpeak)} x2={mx(rPeak)+60} y2={myD(Dpeak)-30} stroke="#c0392b" strokeWidth="1.2"/>
        <rect x={mx(rPeak)+62} y={myD(Dpeak)-52} width={190} height={38} rx={3} fill="#FEF0F0" stroke="#c0392b" strokeWidth="1"/>
        <text x={mx(rPeak)+68} y={myD(Dpeak)-36} fontSize="11" fill="#c0392b" fontWeight="700">② Peak: r* = √(1−2ζ²) = {rPeak.toFixed(3)}</text>
        <text x={mx(rPeak)+68} y={myD(Dpeak)-20} fontSize="11" fill="#444">D_max = 1/(2ζ√(1−ζ²)) = {Dpeak.toFixed(2)}</text>

        {/* ③ Half-power level */}
        <line x1={mx(r1)} y1={myD(Dhp)} x2={mx(r2)} y2={myD(Dhp)} stroke="#27ae60" strokeWidth="2.5"/>
        <circle cx={mx(r1)} cy={myD(Dhp)} r={5} fill="#27ae60" stroke="#fff" strokeWidth="1.5"/>
        <circle cx={mx(r2)} cy={myD(Dhp)} r={5} fill="#27ae60" stroke="#fff" strokeWidth="1.5"/>
        <line x1={mx(r1)} y1={gY+gH} x2={mx(r1)} y2={myD(Dhp)} stroke="#27ae60" strokeWidth="1" strokeDasharray="3"/>
        <line x1={mx(r2)} y1={gY+gH} x2={mx(r2)} y2={myD(Dhp)} stroke="#27ae60" strokeWidth="1" strokeDasharray="3"/>
        <text x={mx(r1)} y={gY+gH+26} textAnchor="middle" fontSize="10" fill="#27ae60">r₁={r1.toFixed(3)}</text>
        <text x={mx(r2)} y={gY+gH+26} textAnchor="middle" fontSize="10" fill="#27ae60">r₂={r2.toFixed(3)}</text>
        {/* Bandwidth arrow */}
        <line x1={mx(r1)} y1={myD(Dhp)+18} x2={mx(r2)} y2={myD(Dhp)+18} stroke="#27ae60" strokeWidth="2" markerEnd="url(#mGrn)"/>
        <line x1={mx(r2)} y1={myD(Dhp)+18} x2={mx(r1)} y2={myD(Dhp)+18} stroke="#27ae60" strokeWidth="2" markerEnd="url(#mGrn)"/>
        <rect x={(mx(r1)+mx(r2))/2-52} y={myD(Dhp)+8} width={104} height={18} rx={3} fill="#f0fff4" stroke="#27ae60" strokeWidth="1"/>
        <text x={(mx(r1)+mx(r2))/2} y={myD(Dhp)+21} textAnchor="middle" fontSize="11" fill="#27ae60" fontWeight="700">③ Δr = r₂−r₁ = 2ζ = {(2*z).toFixed(2)}</text>
        {/* D_hp label */}
        <text x={gX-6} y={myD(Dhp)+4} textAnchor="end" fontSize="10" fill="#27ae60" fontWeight="700">D_max/√2</text>

        {/* ④ Q factor annotation */}
        <line x1={mx(1)} y1={myD(Dd(1,z))} x2={mx(1)+30} y2={myD(Dd(1,z))-30} stroke="#9b59b6" strokeWidth="1.5"/>
        <rect x={mx(1)+32} y={myD(Dd(1,z))-52} width={152} height={36} rx={3} fill="#f5d0ff" stroke="#9b59b6" strokeWidth="1"/>
        <text x={mx(1)+38} y={myD(Dd(1,z))-36} fontSize="11" fill="#9b59b6" fontWeight="700">④ At r=1: D = 1/(2ζ) = {(1/(2*z)).toFixed(1)}</text>
        <text x={mx(1)+38} y={myD(Dd(1,z))-20} fontSize="11" fill="#444">Q = 1/(2ζ) = {(1/(2*z)).toFixed(1)}</text>
        <circle cx={mx(1)} cy={myD(Dd(1,z))} r={5} fill="#9b59b6" stroke="#fff" strokeWidth="1.5"/>

        {/* Axes */}
        <line x1={gX-2} y1={gY} x2={gX-2} y2={gY+gH+2} stroke="#555" strokeWidth="1.5"/>
        <line x1={gX-2} y1={gY+gH+2} x2={gX+gW+2} y2={gY+gH+2} stroke="#555" strokeWidth="1.5"/>
        <text x={18} y={gY+gH/2} textAnchor="middle" fontSize="11" fill="#555" transform={`rotate(-90,18,${gY+gH/2})`}>Magnification D(r)</text>
        <text x={gX+gW/2} y={gY+gH+40} textAnchor="middle" fontSize="11" fill="#555">Frequency ratio  r = ω/ωₙ  [ζ = {z} shown]</text>

        {/* Right panel */}
        <rect x={600} y={10} width={292} height={H-15} rx={5} fill="#fff" stroke="#f0cece"/>
        <text x={746} y={28} textAnchor="middle" fontSize="13" fill="#7B0D0D" fontWeight="700">Summary of Key Values</text>
        {[
          { n:'①', c:'#2E75B6', lbl:'Static limit  (r=0)',     val:'D = 1,  X = F\u2080/k' },
          { n:'②', c:'#c0392b', lbl:'Peak amplitude',          val:`r* = \u221A(1\u22122\u03B6\u00B2) = ${rPeak.toFixed(3)}` },
          { n:'  ', c:'#c0392b', lbl:'  D_max',                 val:`1/(2\u03B6\u221A(1\u2212\u03B6\u00B2)) = ${Dpeak.toFixed(2)}` },
          { n:'③', c:'#27ae60', lbl:'Half-power BW',            val:`\u0394r = 2\u03B6 = ${(2*z).toFixed(2)}` },
          { n:'  ', c:'#27ae60', lbl:'  Damping from test:',    val:'\u03B6 = \u0394\u03C9/(2\u03C9\u2099)' },
          { n:'④', c:'#9b59b6', lbl:'At r=1: D = Q = 1/(2\u03B6)',val:`Q = ${(1/(2*z)).toFixed(1)}` },
          { n:'  ', c:'#9b59b6', lbl:'  Peak force transm.:',  val:`TR = ${(1/(2*z)).toFixed(1)} at r=1` },
        ].map(({n,c,lbl,val},i) => (
          <g key={i}>
            <text x={608} y={48+i*36} fontSize="13" fill={c} fontWeight="700">{n}</text>
            <text x={626} y={48+i*36} fontSize="11" fill="#7B0D0D" fontWeight="700">{lbl}</text>
            <text x={626} y={62+i*36} fontSize="11" fill="#444" fontFamily="'Courier New',monospace">{val}</text>
          </g>
        ))}
      </svg>
      <div style={S.dCaption}>
        Four key points on the magnification curve (ζ={z} shown): ① static deflection D=1 at r=0; ② the amplitude peak at r*=√(1−2ζ²), just below resonance; ③ the half-power bandwidth Δr=2ζ between the two points where amplitude falls to D_max/√2 — the standard experimental method to measure ζ; ④ the value at r=1 exactly, D=Q=1/(2ζ). Note r* is slightly less than 1; for ζ&lt;0.2 the difference is negligible in practice.
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// Diagram 4: Design strategies — detuning, damping, absorber
// ─────────────────────────────────────────────────────────────────
function DiagDesignStrategies() {
  const W = 900, H = 420;
  const nPts = 400, rMax = 3.0;
  const Dd  = (r,z) => 1/Math.sqrt((1-r*r)**2+(2*z*r)**2);
  const mx  = (r,x0,w) => x0+(r/rMax)*w;
  const myD = (D,y0,h,dmax) => y0+h-(Math.min(D,dmax)/dmax)*h;

  // Dynamic absorber: combined response (simplified)
  const absorberD = (r, mu=0.1) => {
    const ra=r, num=(ra*ra-1)**2, den=((1-ra*ra)*(1+mu*0-ra*ra)-mu*ra*ra)**2+0;
    // Simplified 2-DOF absorber response at main mass, tuned absorber
    const rA=1.0, za=0.0;
    const d1=(1-ra*ra);
    const d2=(rA*rA-ra*ra);
    const numer=Math.abs(d2);
    const denom=Math.sqrt((d1*d2 - mu*ra*ra*rA*rA)**2 + (2*za*rA*ra*(d1+mu*rA*rA))**2);
    return numer/(denom+0.0001);
  };

  const panels = [
    {
      x: 10, w: 266, title: 'Strategy 1: Detuning',
      subtitle: 'Change ωₙ away from ω_excitation',
      curves: [
        { label:'Original ωₙ', z:0.05, rShift:0,    c:'#c0392b', dash:'' },
        { label:'Stiffer (+k)', z:0.05, rShift:-0.35, c:'#2E75B6', dash:'' },
        { label:'Heavier (+m)', z:0.05, rShift:+0.4,  c:'#27ae60', dash:'' },
      ],
      note:'Move peak away from operating r',
      sub: 'Keep |r−1| > 0.3 minimum',
    },
    {
      x: 308, w: 266, title: 'Strategy 2: Add Damping',
      subtitle: 'Increase ζ to reduce peak height',
      curves: [
        { label:'ζ = 0.02', z:0.02, rShift:0, c:'#9b59b6', dash:'' },
        { label:'ζ = 0.1',  z:0.10, rShift:0, c:'#c0392b', dash:'' },
        { label:'ζ = 0.3',  z:0.30, rShift:0, c:'#e07b00', dash:'' },
        { label:'ζ = 0.7',  z:0.70, rShift:0, c:'#27ae60', dash:'' },
      ],
      note:'Higher ζ → lower, broader peak',
      sub: 'Target ζ ≥ 0.1 near resonance',
    },
    {
      x: 606, w: 284, title: 'Strategy 3: Dynamic Absorber',
      subtitle: 'Add tuned mass-spring to create anti-resonance',
      curves: [
        { label:'Without absorber', z:0.05, rShift:0, c:'#c0392b', dash:'' },
      ],
      absorber: true,
      note:'Anti-resonance dip at ω_absorber',
      sub: 'Creates two new peaks either side',
    },
  ];

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Resonance Avoidance — Three Engineering Design Strategies</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{width:'100%',display:'block'}}>
        <Arr/>
        {panels.map(({x,w,title,subtitle,curves,absorber,note,sub}) => {
          const gx=x+10, gy=60, gw=w-20, gh=200;
          const lmx = r => mx(r,gx,gw);
          const lmy = D => myD(D,gy,gh,8);

          return (
            <g key={x}>
              <rect x={x} y={10} width={w} height={H-20} rx={5} fill="#fff" stroke="#f0cece"/>
              <text x={x+w/2} y={30} textAnchor="middle" fontSize="13" fill="#7B0D0D" fontWeight="700">{title}</text>
              <text x={x+w/2} y={46} textAnchor="middle" fontSize="10" fill="#555" fontStyle="italic">{subtitle}</text>

              {/* Graph area */}
              <rect x={gx} y={gy} width={gw} height={gh} rx={3} fill="#fafafa" stroke="#ddd"/>

              {/* Grid */}
              {[0,1,2,4,6,8].map(v => (
                <g key={v}>
                  <line x1={gx} y1={lmy(v)} x2={gx+gw} y2={lmy(v)} stroke={v===1?'#ccc':'#ebebeb'} strokeWidth={v===1?1.2:0.6} strokeDasharray={v===1?'4,3':''}/>
                  <text x={gx-4} y={lmy(v)+4} textAnchor="end" fontSize="9" fill="#aaa">{v}</text>
                </g>
              ))}
              {[0,1,2,3].map(r => (
                <g key={r}>
                  <line x1={lmx(r)} y1={gy} x2={lmx(r)} y2={gy+gh} stroke="#ebebeb" strokeWidth="0.6"/>
                  <text x={lmx(r)} y={gy+gh+12} textAnchor="middle" fontSize="9" fill="#aaa">{r}</text>
                </g>
              ))}

              {/* Operating point marker */}
              <line x1={lmx(0.7)} y1={gy} x2={lmx(0.7)} y2={gy+gh} stroke="#555" strokeWidth="1.5" strokeDasharray="3,3"/>
              <text x={lmx(0.7)} y={gy-5} textAnchor="middle" fontSize="9" fill="#555" fontWeight="700">ω_op</text>

              {/* Curves */}
              {curves.map(({z,rShift,c,label}) => {
                const path = Array.from({length:nPts+1},(_,i) => {
                  const r0 = i/nPts*rMax;
                  const rEff = r0 + rShift;
                  if (rEff<=0) return `${i===0?'M':'L'}${lmx(r0).toFixed(1)},${lmy(0.01).toFixed(1)}`;
                  const D = Dd(rEff,z);
                  return `${i===0?'M':'L'}${lmx(r0).toFixed(1)},${lmy(D).toFixed(1)}`;
                }).join(' ');
                return <path key={label} d={path} fill="none" stroke={c} strokeWidth="2"/>;
              })}

              {/* Absorber anti-resonance dip */}
              {absorber && (() => {
                const absPath = Array.from({length:nPts+1},(_,i) => {
                  const r=i/nPts*rMax;
                  // simple 2-peak pattern with dip at r=1
                  const mu=0.08;
                  const r_a=1;
                  const numer=(r_a*r_a-r*r)**2;
                  const d1=(1-r*r), d2=(r_a*r_a-r*r);
                  const real=d1*d2 - mu*r*r;
                  const D=Math.sqrt(numer)/Math.sqrt(real*real+0.0001);
                  return `${i===0?'M':'L'}${lmx(r).toFixed(1)},${lmy(Math.min(D,8)).toFixed(1)}`;
                }).join(' ');
                return (
                  <>
                    <path d={absPath} fill="none" stroke="#2E75B6" strokeWidth="2"/>
                    {/* anti-resonance dip annotation */}
                    <circle cx={lmx(1)} cy={lmy(0)} r={4} fill="#2E75B6" stroke="#fff" strokeWidth="1.5"/>
                    <text x={lmx(1)+5} y={lmy(0)-6} fontSize="9" fill="#2E75B6" fontWeight="700">anti-res.</text>
                  </>
                );
              })()}

              {/* Legend */}
              {curves.map(({c,label},i) => (
                <g key={i}>
                  <line x1={gx+4} y1={gy+gh+22+i*14} x2={gx+22} y2={gy+gh+22+i*14} stroke={c} strokeWidth="2.5"/>
                  <text x={gx+25} y={gy+gh+26+i*14} fontSize="10" fill={c} fontWeight="700">{label}</text>
                </g>
              ))}
              {absorber && (
                <g>
                  <line x1={gx+4} y1={gy+gh+36} x2={gx+22} y2={gy+gh+36} stroke="#2E75B6" strokeWidth="2.5"/>
                  <text x={gx+25} y={gy+gh+40} fontSize="10" fill="#2E75B6" fontWeight="700">With absorber</text>
                </g>
              )}

              {/* Note box */}
              <rect x={x+8} y={H-62} width={w-16} height={48} rx={3} fill="#FEF0F0" stroke="#c0392b" strokeWidth="1"/>
              <text x={x+14} y={H-46} fontSize="11" fill="#7B0D0D" fontWeight="700">{note}</text>
              <text x={x+14} y={H-30} fontSize="10" fill="#555">{sub}</text>
            </g>
          );
        })}
      </svg>
      <div style={S.dCaption}>
        Three strategies to avoid resonance damage. Left: detuning — shift ωₙ by changing stiffness (stiffer moves peak left) or mass (heavier moves peak right), keeping the operating frequency ratio r away from 1. Middle: add damping — higher ζ reduces and broadens the peak; even modest damping (ζ=0.1) dramatically cuts the peak from ~25× to ~5× static deflection. Right: dynamic absorber — a secondary mass-spring tuned to ωₙ creates an anti-resonance dip at the problem frequency, but introduces two new peaks that must also be considered.
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// Diagram 5: Avoiding resonance — operating margin diagram
// ─────────────────────────────────────────────────────────────────
function DiagDesignMargin() {
  const W = 900, H = 320;

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Resonance Margin — Operating Frequency vs Natural Frequency</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{width:'100%',display:'block'}}>
        <Arr/>

        {/* ── Main frequency band diagram ── */}
        <rect x={10} y={10} width={W-20} height={220} rx={5} fill="#fff" stroke="#f0cece"/>
        <text x={W/2} y={30} textAnchor="middle" fontSize="14" fill="#7B0D0D" fontWeight="700">Frequency Band Classification for Design</text>

        {/* Frequency axis */}
        <line x1={50} y1={160} x2={860} y2={160} stroke="#555" strokeWidth="2" markerEnd="url(#mGry)"/>
        <text x={865} y={164} fontSize="12" fill="#555">f</text>

        {/* ωn marker */}
        <line x1={440} y1={100} x2={440} y2={175} stroke="#c0392b" strokeWidth="2.5" strokeDasharray="5,3"/>
        <text x={440} y={96} textAnchor="middle" fontSize="13" fill="#c0392b" fontWeight="700">fₙ = ωₙ/2π</text>

        {/* Danger zone (red): ±20% of fn */}
        <rect x={352} y={114} width={176} height={46} rx={4} fill="#c0392b" opacity="0.15"/>
        <rect x={352} y={114} width={176} height={46} rx={4} fill="none" stroke="#c0392b" strokeWidth="1.5" strokeDasharray="5,3"/>
        <text x={440} y={133} textAnchor="middle" fontSize="11" fill="#c0392b" fontWeight="700">DANGER ZONE</text>
        <text x={440} y={149} textAnchor="middle" fontSize="10" fill="#c0392b">0.8fₙ to 1.2fₙ</text>

        {/* Caution zone (orange): 0.6-0.8 and 1.2-1.5 */}
        <rect x={264} y={118} width={88} height={38} rx={3} fill="#e07b00" opacity="0.12"/>
        <rect x={264} y={118} width={88} height={38} rx={3} fill="none" stroke="#e07b00" strokeWidth="1.2"/>
        <text x={308} y={135} textAnchor="middle" fontSize="10" fill="#e07b00" fontWeight="700">CAUTION</text>
        <text x={308} y={148} textAnchor="middle" fontSize="9" fill="#e07b00">0.6–0.8fₙ</text>

        <rect x={528} y={118} width={132} height={38} rx={3} fill="#e07b00" opacity="0.12"/>
        <rect x={528} y={118} width={132} height={38} rx={3} fill="none" stroke="#e07b00" strokeWidth="1.2"/>
        <text x={594} y={135} textAnchor="middle" fontSize="10" fill="#e07b00" fontWeight="700">CAUTION</text>
        <text x={594} y={148} textAnchor="middle" fontSize="9" fill="#e07b00">1.2–1.5fₙ</text>

        {/* Safe zones (green) */}
        <rect x={60} y={122} width={204} height={30} rx={3} fill="#27ae60" opacity="0.1"/>
        <rect x={60} y={122} width={204} height={30} rx={3} fill="none" stroke="#27ae60" strokeWidth="1.2"/>
        <text x={162} y={141} textAnchor="middle" fontSize="10" fill="#27ae60" fontWeight="700">SAFE  (quasi-static)  f &lt; 0.6fₙ</text>

        <rect x={660} y={122} width={190} height={30} rx={3} fill="#27ae60" opacity="0.1"/>
        <rect x={660} y={122} width={190} height={30} rx={3} fill="none" stroke="#27ae60" strokeWidth="1.2"/>
        <text x={755} y={141} textAnchor="middle" fontSize="10" fill="#27ae60" fontWeight="700">SAFE (isolation)  f &gt; 1.5fₙ</text>

        {/* Frequency tick marks */}
        {[0.4,0.6,0.8,1.0,1.2,1.5,1.8].map(mult => {
          const xp = 440 + (mult-1)*440;
          return (
            <g key={mult}>
              <line x1={xp} y1={157} x2={xp} y2={163} stroke="#555" strokeWidth="1.5"/>
              <text x={xp} y={176} textAnchor="middle" fontSize="9" fill="#888">{mult}fₙ</text>
            </g>
          );
        })}

        {/* Example operating points */}
        {[
          { f:0.3, label:'Machine at\nlow speed', color:'#27ae60' },
          { f:0.72, label:'Approaching\nresonance!', color:'#e07b00' },
          { f:1.0, label:'AT\nRESONANCE', color:'#c0392b' },
          { f:1.8, label:'Operating above\n(isolation zone)', color:'#27ae60' },
        ].map(({f,label,color}) => {
          const xp = 440 + (f-1)*440;
          return (
            <g key={f}>
              <circle cx={xp} cy={160} r={7} fill={color} stroke="#fff" strokeWidth="1.5"/>
              {label.split('\n').map((l,i) => (
                <text key={i} x={xp} y={196+i*13} textAnchor="middle" fontSize="9" fill={color} fontWeight="700">{l}</text>
              ))}
            </g>
          );
        })}

        {/* ── Bottom: practical rules ── */}
        <rect x={10} y={238} width={W-20} height={72} rx={5} fill="#fafafa" stroke="#f0cece"/>
        <text x={W/2} y={256} textAnchor="middle" fontSize="13" fill="#7B0D0D" fontWeight="700">Practical Design Rules</text>
        {[
          { x:20,  c:'#27ae60', txt:'f_op < 0.6fₙ: quasi-static, D ≈ 1 — design for static load only' },
          { x:20,  c:'#e07b00', txt:'0.6fₙ < f_op < 0.8fₙ: check amplitude, add damping if needed' },
          { x:460, c:'#c0392b', txt:'0.8fₙ < f_op < 1.2fₙ: AVOID — or must have ζ ≥ 0.15 minimum' },
          { x:460, c:'#27ae60', txt:'f_op > 1.5fₙ: isolation zone — reduce mount stiffness, min damping' },
        ].map(({x,c,txt},i) => (
          <g key={i}>
            <circle cx={x+14} cy={270+(i%2)*18} r={4} fill={c}/>
            <text x={x+24} y={274+(i%2)*18} fontSize="10" fill="#444">{txt}</text>
          </g>
        ))}
      </svg>
      <div style={S.dCaption}>
        The frequency band diagram shows four operating zones relative to the natural frequency fₙ. The danger zone (0.8–1.2fₙ) must be avoided in steady-state operation unless substantial damping is present. The caution zones require dynamic analysis. Below 0.6fₙ the system behaves quasi-statically (amplification ≈ 1); above 1.5fₙ the system is in the isolation region where amplitude falls below static deflection.
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// Diagram 6: Experimental identification — sweep test & impact
// ─────────────────────────────────────────────────────────────────
function DiagExperimentalSweep() {
  const W = 900, H = 400;
  const z = 0.08, nPts = 600, rMax = 2.0;
  const Dd  = (r,z) => 1/Math.sqrt((1-r*r)**2+(2*z*r)**2);
  const Ph  = (r,z) => { let p=Math.atan2(2*z*r,1-r*r); return p<0?p+Math.PI:p; };

  const gX=55, gY=30, gW=450, gH=160;
  const phY=210, phH=110;
  const mx = r => gX+(r/rMax)*gW;
  const myA = D => gY+(1-Math.min(D,10)/10)*gH;
  const myP = p => phY+(p/Math.PI)*phH;

  const Dpeak = Dd(Math.sqrt(1-2*z*z),z);
  const Dhp   = Dpeak/Math.sqrt(2);
  let r1=0, r2=0;
  for(let i=1;i<=nPts;i++){
    const ra=(i-1)/nPts*rMax, rb=i/nPts*rMax;
    if(Dd(ra,z)<Dhp&&Dd(rb,z)>=Dhp&&ra<1) r1=rb;
    if(Dd(ra,z)>=Dhp&&Dd(rb,z)<Dhp&&ra>1) r2=ra;
  }

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Experimental Identification — Frequency Sweep Test</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{width:'100%',display:'block'}}>
        <Arr/>

        {/* ── Amplitude plot ── */}
        <text x={gX} y={gY-6} fontSize="12" fill="#7B0D0D" fontWeight="700">Step 1: Measure FRF (amplitude)</text>
        <rect x={gX} y={gY} width={gW} height={gH} rx={3} fill="#fafafa" stroke="#ddd"/>
        {[0,1,2,4,6,8,10].map(v=>(
          <g key={v}>
            <line x1={gX} y1={myA(v)} x2={gX+gW} y2={myA(v)} stroke={v===1?'#ccc':'#ebebeb'} strokeWidth={v===1?1.2:0.6}/>
            <text x={gX-4} y={myA(v)+4} textAnchor="end" fontSize="9" fill="#888">{v}</text>
          </g>
        ))}
        {[0,0.5,1,1.5,2].map(r=>(
          <g key={r}>
            <line x1={mx(r)} y1={gY} x2={mx(r)} y2={gY+gH} stroke="#ebebeb" strokeWidth="0.6"/>
          </g>
        ))}
        {/* FRF curve */}
        <path d={Array.from({length:nPts+1},(_,i)=>{const r=i/nPts*rMax;return `${i===0?'M':'L'}${mx(r).toFixed(1)},${myA(Dd(r,z)).toFixed(1)}`;}).join(' ')} fill="none" stroke="#c0392b" strokeWidth="2.5"/>

        {/* Annotations on amplitude */}
        {/* Peak */}
        <circle cx={mx(Math.sqrt(1-2*z*z))} cy={myA(Dpeak)} r={5} fill="#c0392b" stroke="#fff" strokeWidth="1.5"/>
        <line x1={mx(Math.sqrt(1-2*z*z))} y1={myA(Dpeak)} x2={mx(Math.sqrt(1-2*z*z))} y2={gY+gH} stroke="#c0392b" strokeWidth="1" strokeDasharray="3"/>
        <text x={mx(Math.sqrt(1-2*z*z))} y={gY+gH+12} textAnchor="middle" fontSize="10" fill="#c0392b" fontWeight="700">① Read ω_peak</text>

        {/* Half-power points */}
        <line x1={mx(r1)} y1={myA(Dhp)} x2={mx(r2)} y2={myA(Dhp)} stroke="#27ae60" strokeWidth="2" markerEnd="url(#mGrn)"/>
        <line x1={mx(r2)} y1={myA(Dhp)} x2={mx(r1)} y2={myA(Dhp)} stroke="#27ae60" strokeWidth="2" markerEnd="url(#mGrn)"/>
        <circle cx={mx(r1)} cy={myA(Dhp)} r={4} fill="#27ae60" stroke="#fff" strokeWidth="1.5"/>
        <circle cx={mx(r2)} cy={myA(Dhp)} r={4} fill="#27ae60" stroke="#fff" strokeWidth="1.5"/>
        <text x={(mx(r1)+mx(r2))/2} y={myA(Dhp)-8} textAnchor="middle" fontSize="10" fill="#27ae60" fontWeight="700">② Half-power: ζ = Δω/(2ωₙ)</text>
        <line x1={mx(r1)} y1={gY+gH} x2={mx(r1)} y2={myA(Dhp)+4} stroke="#27ae60" strokeWidth="1" strokeDasharray="3"/>
        <line x1={mx(r2)} y1={gY+gH} x2={mx(r2)} y2={myA(Dhp)+4} stroke="#27ae60" strokeWidth="1" strokeDasharray="3"/>

        {/* D=1 reference */}
        <line x1={gX} y1={myA(1)} x2={gX+gW} y2={myA(1)} stroke="#2E75B6" strokeWidth="1.2" strokeDasharray="5,3"/>
        <text x={gX+gW+4} y={myA(1)+4} fontSize="9" fill="#2E75B6">③ D=1 gives k = F₀/X_static</text>

        {/* ── Phase plot ── */}
        <text x={gX} y={phY-6} fontSize="12" fill="#7B0D0D" fontWeight="700">Step 2: Read phase</text>
        <rect x={gX} y={phY} width={gW} height={phH} rx={3} fill="#fafafa" stroke="#ddd"/>
        {[0,90,180].map(deg=>(
          <g key={deg}>
            <line x1={gX} y1={myP(deg*Math.PI/180)} x2={gX+gW} y2={myP(deg*Math.PI/180)} stroke={deg===90?'#ccc':'#ebebeb'} strokeWidth={deg===90?1.5:0.6}/>
            <text x={gX-4} y={myP(deg*Math.PI/180)+4} textAnchor="end" fontSize="9" fill="#888">{deg}°</text>
          </g>
        ))}
        <path d={Array.from({length:nPts+1},(_,i)=>{const r=i/nPts*rMax;return `${i===0?'M':'L'}${mx(r).toFixed(1)},${myP(Ph(r,z)).toFixed(1)}`;}).join(' ')} fill="none" stroke="#9b59b6" strokeWidth="2.5"/>
        {/* 90° crossing */}
        <circle cx={mx(1)} cy={myP(Math.PI/2)} r={5} fill="#c0392b" stroke="#fff" strokeWidth="1.5"/>
        <line x1={mx(1)} y1={phY} x2={mx(1)} y2={phY+phH} stroke="#c0392b" strokeWidth="1.5" strokeDasharray="4,3"/>
        <text x={mx(1)+5} y={myP(Math.PI/2)-6} fontSize="10" fill="#c0392b" fontWeight="700">④ Phase = 90° → ωₙ exactly</text>
        {[0,0.5,1,1.5,2].map(r=>(
          <g key={r}>
            <line x1={mx(r)} y1={phY+phH} x2={mx(r)} y2={phY+phH+4} stroke="#555" strokeWidth="1.5"/>
            <text x={mx(r)} y={phY+phH+14} textAnchor="middle" fontSize="10" fill="#888">{r}</text>
          </g>
        ))}
        <text x={gX+gW/2} y={phY+phH+28} textAnchor="middle" fontSize="11" fill="#555">r = ω / ω_n</text>
        <line x1={gX-2} y1={phY} x2={gX-2} y2={phY+phH+2} stroke="#555" strokeWidth="1.5"/>

        {/* ── RIGHT: step-by-step procedure ── */}
        <rect x={520} y={10} width={370} height={H-20} rx={5} fill="#fff" stroke="#f0cece"/>
        <text x={705} y={28} textAnchor="middle" fontSize="13" fill="#7B0D0D" fontWeight="700">Sweep Test Procedure</text>

        {[
          { n:'①', c:'#c0392b', title:'Apply harmonic force at frequency ω',
            lines:['Use shaker or rotating imbalance','Vary ω slowly (stepped or continuous)','Record steady-state amplitude X at each ω','Calculate D = X/(F₀/k)'] },
          { n:'②', c:'#27ae60', title:'Find ωₙ from amplitude peak',
            lines:['Read ω at peak of D(ω) curve','ωₙ ≈ ω_peak  (exact for ζ=0; ωₙ = ω_peak/√(1−2ζ²))','For ζ < 0.1: error < 1%'] },
          { n:'③', c:'#2E75B6', title:'Measure ζ from half-power bandwidth',
            lines:['Find ω₁, ω₂ where X = X_max/√2','ζ = (ω₂−ω₁)/(2ωₙ)','Also: ζ ≈ 1/(2Q) where Q=D_max'] },
          { n:'④', c:'#9b59b6', title:'Confirm ωₙ from phase',
            lines:['Phase = 90° exactly at r = 1  (all ζ)','More reliable than amplitude peak','Use for final verification of ωₙ'] },
          { n:'⑤', c:'#e07b00', title:'Extract stiffness k',
            lines:['k = F₀ / X_static  (at r → 0)','Or: k = m·ωₙ²  if mass known','Gives full SDOF model: m, k, c'] },
        ].map(({n,c,title,lines},i) => (
          <g key={i}>
            <circle cx={533} cy={50+i*68} r={11} fill={c}/>
            <text x={533} y={55+i*68} textAnchor="middle" fontSize="12" fill="#fff" fontWeight="700">{n}</text>
            <text x={552} y={50+i*68} fontSize="11" fill="#7B0D0D" fontWeight="700">{title}</text>
            {lines.map((l,j) => <text key={j} x={552} y={64+j*14+i*68} fontSize="10" fill="#444">{l}</text>)}
          </g>
        ))}
      </svg>
      <div style={S.dCaption}>
        Frequency sweep test: apply a sinusoidal force at increasing frequencies, measure steady-state amplitude and phase at each. Four key readings: ① amplitude peak identifies ω_peak ≈ ωₙ; ② half-power bandwidth Δω = ω₂−ω₁ gives ζ = Δω/(2ωₙ); ③ D=1 at low frequency gives k directly; ④ phase = 90° crossing gives the most reliable identification of ωₙ, independent of damping and measurement noise.
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// Diagram 7: Impact hammer test & Nyquist circle
// ─────────────────────────────────────────────────────────────────
function DiagImpactTest() {
  const W = 900, H = 400;
  const z = 0.1;

  // Impulse response: h(t) = e^(-zωnt)sin(ωdt)/(mωd)
  const ωn=2*Math.PI, ωd=ωn*Math.sqrt(1-z*z), dt=0.005, tMax=3;
  const impPts=[];
  for(let t=0;t<=tMax;t+=dt) impPts.push([t, Math.exp(-z*ωn*t)*Math.sin(ωd*t)]);

  const tx = t => 58+(t/tMax)*350;
  const hy = y => 148-y*70;

  // Nyquist circle: H(ω) = 1/(k-mω²+jcω) = (1/k)·1/((1-r²)+j2ζr)
  // Re = (1-r²)/D²k,  Im = -2ζr/D²k  (with k=1 normalised)
  const nqPts=[];
  for(let r=0;r<=3;r+=0.01){
    const D2=(1-r*r)**2+(2*z*r)**2;
    nqPts.push([(1-r*r)/D2, -2*z*r/D2]);
  }
  const nqScale=120, nqCx=760, nqCy=190;

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Impact Hammer Test &amp; Nyquist Circle Method</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{width:'100%',display:'block'}}>
        <Arr/>

        {/* ── LEFT: impulse test setup ── */}
        <rect x={10} y={10} width={420} height={H-20} rx={5} fill="#fff" stroke="#f0cece"/>
        <text x={220} y={28} textAnchor="middle" fontSize="13" fill="#7B0D0D" fontWeight="700">Impact Hammer Test — Free Decay</text>

        {/* Simple structure diagram */}
        {(() => {
          const bx=100, by=68;
          return (
            <>
              {/* Base */}
              <line x1={60} y1={by+50} x2={190} y2={by+50} stroke="#555" strokeWidth="2"/>
              {[60,80,100,120,140,160,180].map(x=>(<line key={x} x1={x} y1={by+50} x2={x-10} y2={by+64} stroke="#555" strokeWidth="1.5"/>))}
              {/* Columns */}
              <rect x={70} y={by-10} width={8} height={60} rx={2} fill="#888"/>
              <rect x={175} y={by-10} width={8} height={60} rx={2} fill="#888"/>
              {/* Beam/mass */}
              <rect x={68} y={by-18} width={117} height={22} rx={3} fill="#2E75B6" stroke="#1a5276" strokeWidth="2"/>
              <text x={127} y={by-3} textAnchor="middle" fontSize="12" fill="#fff" fontWeight="700">Structure</text>
              {/* Hammer */}
              <rect x={38} y={by-28} width={22} height={14} rx={3} fill="#e07b00" stroke="#b7510a" strokeWidth="2"/>
              <rect x={20} y={by-25} width={18} height={8} rx={2} fill="#888"/>
              <text x={29} y={by-34} textAnchor="middle" fontSize="10" fill="#e07b00" fontWeight="700">hammer</text>
              <line x1={60} y1={by-21} x2={68} y2={by-10} stroke="#e07b00" strokeWidth="2.5" markerEnd="url(#mOrg)"/>
              {/* Accelerometer */}
              <rect x={162} y={by-34} width={24} height={14} rx={2} fill="#9b59b6" stroke="#7d3c98" strokeWidth="1.5"/>
              <text x={174} y={by-25} textAnchor="middle" fontSize="9" fill="#fff" fontWeight="700">acc.</text>
              <line x1={174} y1={by-18} x2={174} y2={by-20} stroke="#9b59b6" strokeWidth="1.5"/>
            </>
          );
        })()}

        {/* Impulse force spike */}
        <text x={58} y={100} fontSize="11" fill="#e07b00" fontWeight="700">Force F(t):</text>
        <line x1={58} y1={114} x2={408} y2={114} stroke="#ddd" strokeWidth="1"/>
        <line x1={58} y1={90} x2={58} y2={115} stroke="#555" strokeWidth="1.5"/>
        <line x1={58} y1={114} x2={408} y2={114} stroke="#555" strokeWidth="1.5"/>
        <line x1={74} y1={114} x2={74} y2={90} stroke="#e07b00" strokeWidth="3"/>
        <line x1={74} y1={90} x2={74+6} y2={114} stroke="#e07b00" strokeWidth="3"/>
        <text x={78} y={88} fontSize="10" fill="#e07b00">δ(t)</text>

        {/* Impulse response waveform */}
        <text x={58} y={136} fontSize="11" fill="#c0392b" fontWeight="700">Response x(t):</text>
        <rect x={56} y={105} width={355} height={85} rx={3} fill="#fafafa" stroke="#e8d0d0"/>
        <line x1={58} y1={hy(0)} x2={408} y2={hy(0)} stroke="#ddd" strokeWidth="1"/>
        <path d={impPts.map(([t,y],i)=>`${i===0?'M':'L'}${tx(t).toFixed(1)},${hy(y).toFixed(1)}`).join(' ')} fill="none" stroke="#c0392b" strokeWidth="2.2"/>

        {/* Envelope */}
        {[1,-1].map(s=>{
          const ep=[]; for(let t=0;t<=tMax;t+=0.05)ep.push([t,s*Math.exp(-z*ωn*t)]);
          return <path key={s} d={ep.map(([t,y],i)=>`${i===0?'M':'L'}${tx(t).toFixed(1)},${hy(y).toFixed(1)}`).join(' ')} fill="none" stroke="#e07b00" strokeWidth="1.5" strokeDasharray="5,3" opacity="0.7"/>;
        })}

        {/* Peak annotations */}
        {[0,1,2].map(cyc => {
          const tPeak = (Math.PI/2 + cyc*2*Math.PI)/ωd;
          if (tPeak > tMax) return null;
          const A = Math.exp(-z*ωn*tPeak);
          return (
            <g key={cyc}>
              <circle cx={tx(tPeak)} cy={hy(A)} r={4} fill="#2E75B6" stroke="#fff" strokeWidth="1.5"/>
              <text x={tx(tPeak)+4} y={hy(A)-4} fontSize="9" fill="#2E75B6" fontWeight="700">x{cyc+1}</text>
            </g>
          );
        })}
        <text x={220} y={200} textAnchor="middle" fontSize="10" fill="#e07b00" fontStyle="italic">envelope: e^(-\u03B6\u03C9\u2099t)</text>

        {/* Time axis */}
        {[0,1,2,3].map(t=>(
          <g key={t}>
            <line x1={tx(t)} y1={189} x2={tx(t)} y2={194} stroke="#555" strokeWidth="1.5"/>
            <text x={tx(t)} y={204} textAnchor="middle" fontSize="9" fill="#888">{t}s</text>
          </g>
        ))}

        {/* Extraction formulas */}
        <rect x={16} y={218} width={408} height={88} rx={3} fill="#fafafa" stroke="#f0cece"/>
        <text x={22} y={235} fontSize="12" fill="#7B0D0D" fontWeight="700">Extract from free decay:</text>
        {[
          ['Damped frequency ωd', 'ωd = 2\u03C0/Td  (period between peaks)'],
          ['Log. decrement \u03B4', '\u03B4 = ln(x\u2081/x\u2082) = 2\u03C0\u03B6/\u221A(1\u2212\u03B6\u00B2)'],
          ['Damping ratio \u03B6', '\u03B6 = \u03B4/\u221A(4\u03C0\u00B2+\u03B4\u00B2)'],
          ['Natural freq. \u03C9\u2099', '\u03C9\u2099 = \u03C9d/\u221A(1\u2212\u03B6\u00B2)'],
        ].map(([k,v],i) => (
          <g key={i}>
            <text x={22} y={251+i*17} fontSize="11" fill="#7B0D0D" fontWeight="600">{k}:</text>
            <text x={185} y={251+i*17} fontSize="11" fill="#444" fontFamily="'Courier New',monospace">{v}</text>
          </g>
        ))}

        {/* ── RIGHT: Nyquist circle ── */}
        <rect x={440} y={10} width={450} height={H-20} rx={5} fill="#fff" stroke="#f0cece"/>
        <text x={665} y={28} textAnchor="middle" fontSize="13" fill="#7B0D0D" fontWeight="700">Nyquist (Circle-Fit) Method</text>

        {/* Nyquist circle plot */}
        <rect x={448} y={38} width={270} height={300} rx={3} fill="#fafafa" stroke="#ddd"/>
        {/* Axes */}
        <line x1={nqCx-130} y1={nqCy} x2={nqCx+110} y2={nqCy} stroke="#888" strokeWidth="1.2"/>
        <line x1={nqCx} y1={nqCy-145} x2={nqCx} y2={nqCy+120} stroke="#888" strokeWidth="1.2"/>
        <text x={nqCx+112} y={nqCy+4} fontSize="10" fill="#888">Re</text>
        <text x={nqCx+4} y={nqCy-148} fontSize="10" fill="#888">Im</text>

        {/* Nyquist circle */}
        <path
          d={nqPts.map(([re,im],i)=>`${i===0?'M':'L'}${(nqCx+re*nqScale).toFixed(1)},${(nqCy+im*nqScale).toFixed(1)}`).join(' ')}
          fill="none" stroke="#c0392b" strokeWidth="2.5"/>

        {/* Key points on Nyquist */}
        {[
          { r:0,   label:'r=0', dy:-12 },
          { r:1,   label:'r=1\n(ωₙ)', dy:14 },
          { r:1.5, label:'r=1.5', dy:-12 },
        ].map(({r,label,dy}) => {
          const D2=(1-r*r)**2+(2*z*r)**2;
          const re=(1-r*r)/D2, im=-2*z*r/D2;
          const cx2=nqCx+re*nqScale, cy2=nqCy+im*nqScale;
          return (
            <g key={r}>
              <circle cx={cx2} cy={cy2} r={5} fill={r===1?'#c0392b':'#2E75B6'} stroke="#fff" strokeWidth="1.5"/>
              {label.split('\n').map((l,i) => (
                <text key={i} x={cx2+(r===1?10:6)} y={cy2+dy+i*13} fontSize="10" fill={r===1?'#c0392b':'#2E75B6'} fontWeight="700">{l}</text>
              ))}
            </g>
          );
        })}

        {/* Diameter annotation */}
        {(() => {
          const top=(nqCx+0), topY=(nqCy+0-1/(2*z)*nqScale);
          const bot=nqCx, botY=nqCy;
          return (
            <>
              <line x1={top-4} y1={topY} x2={bot-4} y2={botY} stroke="#2E75B6" strokeWidth="1.5" strokeDasharray="4,3" markerEnd="url(#mBlue)"/>
              <line x1={bot-4} y1={botY} x2={top-4} y2={topY} stroke="#2E75B6" strokeWidth="1.5" strokeDasharray="4,3" markerEnd="url(#mBlue)"/>
              <text x={top-48} y={(topY+botY)/2} textAnchor="end" fontSize="10" fill="#2E75B6" fontWeight="700">diameter = 1/(c\u03C9\u2099)</text>
            </>
          );
        })()}

        {/* Right: Nyquist explanation */}
        <rect x={726} y={38} width={158} height={300} rx={3} fill="#fafafa" stroke="#f0cece"/>
        <text x={805} y={54} textAnchor="middle" fontSize="11" fill="#7B0D0D" fontWeight="700">Circle-Fit Steps</text>
        {[
          ['1.','Measure H(\u03C9) = X/F'],
          ['  ','at many frequencies'],
          ['2.','Plot Re vs Im: SDOF'],
          ['  ','traces a circle'],
          ['3.','\u03C9\u2099 at topmost point of'],
          ['  ','circle (Im minimum)'],
          ['4.','Diameter = 1/(c\u03C9\u2099)'],
          ['  ','gives c directly'],
          ['5.','Works even with'],
          ['  ','close-spaced modes'],
          ['  ','in multi-DOF tests'],
        ].map(([n,l],i) => (
          <g key={i}>
            <text x={732} y={68+i*21} fontSize="10" fill={n.match(/\d/)?'#c0392b':'#888'} fontWeight={n.match(/\d/)?'700':'400'}>{n}</text>
            <text x={748} y={68+i*21} fontSize="10" fill="#444">{l}</text>
          </g>
        ))}
      </svg>
      <div style={S.dCaption}>
        Two experimental methods. Left: impact hammer test — strike the structure with an instrumented hammer and record the free decay. The decaying oscillation directly gives ωd (period between peaks), ζ (logarithmic decrement from peak ratios), and hence ωₙ. Right: Nyquist (circle-fit) method — plot the FRF as Im(H) vs Re(H). For a single DOF the locus is a perfect circle; ωₙ is at the point of maximum Im (top of circle), and the circle diameter gives the damping coefficient directly. The circle-fit method is the standard tool in experimental modal analysis for separating closely-spaced modes.
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// Diagram 8: Real-world resonance examples
// ─────────────────────────────────────────────────────────────────
function DiagRealWorldExamples() {
  const W = 900, H = 300;

  const examples = [
    {
      x:10, w:168,
      title:'Tacoma Narrows\nBridge (1940)',
      color:'#c0392b',
      bg:'#FEF0F0',
      lines:[
        'Wind vortex shedding',
        'matched torsional ωₙ',
        'Aeroelastic flutter',
        'Mode: 0.2 Hz torsion',
        'Collapsed in 42mph',
        'wind — well below',
        'design wind speed',
        'Fix: stiffen torsion,',
        'add open deck',
      ],
    },
    {
      x:186, w:168,
      title:'Millennium\nBridge London',
      color:'#e07b00',
      bg:'#fff5e6',
      lines:[
        'Opened June 2000',
        'Pedestrian walking:',
        '~1 Hz lateral pace',
        'Bridge fₙ = 1.0 Hz',
        'Crowd synchronised',
        'to bridge motion —',
        'positive feedback',
        'Fix: 37 fluid dampers,',
        '52 TMDs added',
      ],
    },
    {
      x:362, w:168,
      title:'Turbine Blade\nFatigue',
      color:'#9b59b6',
      bg:'#f5d0ff',
      lines:[
        'Blade passes rotor',
        'struts at blade-',
        'passing frequency',
        'BPF = N × RPM/60',
        'Must not coincide',
        'with blade ωₙ modes',
        'Campbell diagram',
        'used to map all',
        'crossings vs RPM',
      ],
    },
    {
      x:538, w:168,
      title:'Washing Machine\nSpin Cycle',
      color:'#27ae60',
      bg:'#f0fff4',
      lines:[
        'Unbalanced load',
        'creates rotating',
        'imbalance force',
        'Must pass through',
        'ωₙ on spin-up',
        'Soft suspension:',
        'low ωₙ ≈ 3 Hz',
        'Quickly transits',
        'resonance at 180 rpm',
      ],
    },
    {
      x:714, w:176,
      title:'Building in\nEarthquake',
      color:'#2E75B6',
      bg:'#eaf3ff',
      lines:[
        'Ground: 0.1–10 Hz',
        'Building ωₙ depends',
        'on height & stiffness',
        'Tall buildings: ~0.1Hz',
        'Short/stiff: ~2-5 Hz',
        'Seismic isolation:',
        'base isolators lower',
        'fₙ to 0.3–0.5 Hz,',
        'isolating from quake',
      ],
    },
  ];

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Real-World Resonance — Engineering Consequences &amp; Fixes</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{width:'100%',display:'block'}}>
        {examples.map(({x,w,title,color,bg,lines}) => (
          <g key={x}>
            <rect x={x} y={10} width={w} height={H-20} rx={5} fill={bg} stroke={color} strokeWidth="1.5"/>
            {/* Color header bar */}
            <rect x={x} y={10} width={w} height={36} rx={5} fill={color}/>
            <rect x={x} y={30} width={w} height={16} fill={color}/>
            {title.split('\n').map((l,i) => (
              <text key={i} x={x+w/2} y={26+i*15} textAnchor="middle" fontSize="11" fill="#fff" fontWeight="700">{l}</text>
            ))}
            {lines.map((l,i) => (
              <text key={i} x={x+8} y={58+i*22} fontSize="10" fill="#333">{l}</text>
            ))}
          </g>
        ))}
      </svg>
      <div style={S.dCaption}>
        Five real engineering resonance events. Each illustrates a different mechanism: aeroelastic flutter (Tacoma), human-structure interaction (Millennium), engine-order excitation (turbines), rotating imbalance run-up (washing machines), and seismic base excitation (buildings). In every case the solution involved either detuning (changing ωₙ), adding damping, or installing a dynamic absorber — the same three strategies shown in the design diagram above.
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// Main export
// ─────────────────────────────────────────────────────────────────
export default function ResonanceFrequencies() {
  return (
    <div style={S.wrapper}>
      <h1 style={S.h1}>Resonance Frequencies</h1>
      <p style={S.p}>
        Resonance is the most important single concept in vibration engineering. It occurs when a system is driven at a frequency that matches its natural frequency — the condition under which energy is most efficiently transferred from the excitation into the vibrating structure. The consequences range from uncomfortable vibration to catastrophic structural failure. This page covers what resonance physically means, how it differs in undamped and damped systems, how to design systems to avoid it, and how to identify natural frequencies experimentally.
      </p>

      <InfoBox title="Core Ideas at a Glance" items={[
        "Resonance condition: ω = ωₙ, i.e. r = ω/ωₙ = 1 — forcing frequency matches natural frequency",
        "Undamped: amplitude grows linearly without bound: x(t) = (F₀/2mωₙ)·t·sin(ωₙt)",
        "Damped: finite peak D_max = 1/(2ζ√(1−ζ²)) at r* = √(1−2ζ²), just below r=1",
        "Phase = 90° exactly at r=1 for ALL values of ζ — the most reliable resonance indicator",
        "Quality factor Q = 1/(2ζ) = D_max ≈ peak amplification at r=1",
        "Half-power bandwidth: ζ = (ω₂−ω₁)/(2ωₙ) — standard experimental damping measurement",
        "Design strategies: detune (move ωₙ away), add damping (ζ≥0.1), or use dynamic absorber",
        "Experimental methods: frequency sweep (FRF), impact hammer (free decay), Nyquist circle-fit",
      ]}/>

      <h2 style={S.h2}>Definition &amp; Physical Meaning</h2>
      <p style={S.p}>
        Resonance is not simply "large vibration" — it is a specific energy condition. At resonance the applied force is always perfectly in phase with the velocity of the mass, which means the force does positive work on the mass every instant of every cycle. Energy is pumped into the system continuously with no portion being returned. Without any dissipation mechanism, this accumulated energy has nowhere to go and the vibration amplitude grows without limit. The key physical insight is that it is the phase relationship between force and velocity — not force and displacement — that controls energy transfer.
      </p>
      <DiagPhysicalMeaning/>
      <Table2 rows={[
        ["Resonance condition","ω = ωₙ  (r = ω/ωₙ = 1)\nForcing frequency = natural frequency of the system"],
        ["Why energy accumulates","At resonance: force F in phase with velocity v\nPower P = F·v > 0 at every instant\nNet work per cycle > 0 → energy accumulates indefinitely"],
        ["Off-resonance behaviour","F and v drift in and out of phase as ω ≠ ωₙ\nNet work per cycle = 0 in steady state\nAmplitude stays bounded (even without damping)"],
        ["Natural frequency ωₙ","ωₙ = √(k/m)  [rad/s]\nDetermined solely by system properties k and m\nIndependent of forcing amplitude F₀ or damping c"],
        ["Undamped resonance solution","x(t) = (F₀/2mωₙ)·t·sin(ωₙt)\nAmplitude envelope grows linearly: A(t) = F₀t/(2mωₙ)\nNo steady state — grows forever"],
        ["Phase at resonance","φ = 90° exactly at r = 1 for ALL values of ζ\nUniversal result — independent of damping\nMost reliable experimental method to locate ωₙ"],
      ]}/>

      <h2 style={S.h2}>Resonance in Undamped vs Damped Systems</h2>
      <p style={S.p}>
        The presence of damping fundamentally changes the character of resonance. In an undamped system the FRF has a vertical asymptote at r=1 and a discontinuous phase jump. In a damped system the peak is finite (height ≈ 1/2ζ), occurs slightly below r=1, and the phase transitions smoothly. The single universal feature that survives in both cases is the 90° phase crossing at exactly r=1 — making it the gold-standard experimental indicator of ωₙ regardless of how much or how little damping is present.
      </p>
      <DiagUndampedVsDamped/>
      <DiagPeakAnatomy/>
      <Table2 rows={[
        ["Undamped peak","D → ∞ at r = 1 (vertical asymptote)\nPhase jumps 0° → 180° discontinuously at r = 1\nNot physically realisable — all real systems have some damping"],
        ["Damped peak amplitude","D_max = 1/(2ζ√(1−ζ²))\nOccurs at r* = √(1−2ζ²)  (slightly below r = 1)\nFor ζ = 0.05: D_max = 10.0;  ζ = 0.1: D_max = 5.03;  ζ = 0.3: D_max = 1.75"],
        ["Peak shift from r=1","r* = √(1−2ζ²)\nFor ζ = 0.1: r* = 0.990 (0.5% below 1)\nFor ζ = 0.3: r* = 0.954 (4.6% below 1) — significant"],
        ["No peak condition","ζ ≥ 1/√2 ≈ 0.707: D decreases monotonically (no resonance peak)\nAmplitude always ≤ static deflection F₀/k for all r\nUseful for critically-damped instruments"],
        ["Quality factor Q","Q = 1/(2ζ)  ≈ D at r = 1 (exactly)\nHigh Q → tall narrow peak (lightly damped, resonance-sensitive)\nLow Q → short broad peak (heavily damped, less sensitive)"],
        ["Half-power bandwidth","Frequencies where D = D_max/√2 (power = half of peak power)\nΔω = ω₂ − ω₁ = 2ζωₙ\nζ = Δω/(2ωₙ) — standard experimental measurement"],
      ]}/>

      <h2 style={S.h2}>Avoiding Resonance in Design</h2>
      <p style={S.p}>
        Engineering design must ensure that operating frequencies do not coincide with natural frequencies, or that damping is sufficient to limit the amplitude if they do. Three strategies are available: detuning (changing the system's natural frequency), adding damping, and installing a dynamic (tuned mass) absorber. Each has tradeoffs and is appropriate in different situations.
      </p>
      <DiagDesignStrategies/>
      <DiagDesignMargin/>
      <Table2 rows={[
        ["Detuning — increase stiffness","k_new = (r_new/r_old)²·k_old\nStiffening raises ωₙ → moves peak to higher r (safer at given ω)\nLimitation: adds weight; may conflict with static strength requirements"],
        ["Detuning — increase mass","Adding mass lowers ωₙ → moves peak to lower r\nUseful when operating frequency is above resonance (isolation zone)\nLimitation: weight penalty; may affect other modes"],
        ["Adding viscous damping","Reduces peak D_max = 1/(2ζ) — doubling ζ halves peak\nWide applicability: viscoelastic mounts, squeeze-film dampers, TMDs\nLimitation: damping reduces isolation at r > √2 (force transmissibility)"],
        ["Dynamic (tuned) absorber","Attach secondary mass m_a and spring k_a tuned so ωₐ = ωₙ\nCreates anti-resonance dip exactly at problem frequency\nMain mass amplitude → 0 at tuned frequency\nLimitation: creates two new peaks either side; needs maintenance; narrow-band"],
        ["Detuning margin rule","Keep |r − 1| > 0.2 for undamped systems\nKeep |r − 1| > 0.1 if ζ > 0.1\nIndustry standard: 20% separation between ωₙ and ω_operating"],
        ["Campbell diagram","Plot all ωₙ modes as horizontal lines vs RPM on x-axis\nOverlay engine-order lines (1×, 2×, 3×, … × RPM)\nCrossings = resonances — avoid sustained operation at those RPMs\nStandard tool in turbomachinery, motors, compressors"],
      ]}/>

      <h2 style={S.h2}>Experimental Identification of Natural Frequencies</h2>
      <p style={S.p}>
        In practice, natural frequencies are rarely known precisely from analysis alone — material properties, boundary conditions, and joint stiffnesses all introduce uncertainty. Experimental modal analysis (EMA) measures the actual system response to extract ωₙ, ζ, and mode shapes from test data. Three methods are standard: the frequency sweep test (FRF measurement), the impact hammer test (free decay), and the Nyquist circle-fit for precise damping extraction.
      </p>
      <DiagExperimentalSweep/>
      <DiagImpactTest/>
      <Table2 rows={[
        ["Frequency sweep test","Apply F₀cos(ωt), vary ω slowly, measure steady-state X(ω)\nFRF: H(ω) = X(ω)/F(ω)  — complex (amplitude + phase)\nωₙ from phase = 90° crossing (most reliable)\nζ from half-power bandwidth: Δω/(2ωₙ)"],
        ["Impact hammer test","Single sharp blow ≈ impulse force δ(t)\nBroad-band excitation: contains all frequencies simultaneously\nMeasure free decay x(t): extract ωd, log-decrement δ\nωd = 2π/Td;  ζ = δ/√(4π²+δ²);  ωₙ = ωd/√(1−ζ²)"],
        ["Log decrement method","δ = ln(x₁/x₂) = 2πζ/√(1−ζ²)\nFor small ζ: δ ≈ 2πζ  (simplified)\nMore peaks → better accuracy: δ = (1/n)·ln(x₁/x_{n+1})"],
        ["Nyquist circle-fit","Plot Im(H) vs Re(H): SDOF → perfect circle\nωₙ at point of maximum Im (bottom of circle in Im-negative convention)\nDiameter = 1/(c·ωₙ) → c directly\nBest for closely-spaced modes in multi-DOF structures"],
        ["Phase resonance method","Tune forcing until phase = exactly 90°\nTrue resonance condition: does not require sweeping through peak\nMost accurate single-frequency method\nUsed in aerospace ground vibration testing (GVT)"],
        ["FRF from random excitation","Apply broadband random force (white noise or burst random)\nCompute H(ω) = S_xF(ω)/S_FF(ω)  (cross/auto spectral density)\nFaster than stepped sine; less accurate for closely-spaced modes\nStandard in production-line vibration testing"],
      ]}/>

      <h2 style={S.h2}>Real-World Examples</h2>
      <DiagRealWorldExamples/>
      <Table2 rows={[
        ["Tacoma Narrows (1940)","Aeroelastic flutter: wind vortex shedding matched torsional ωₙ ≈ 0.2 Hz\nLesson: torsional stiffness and aerodynamic shape matter\nModern bridges use open-grid decks, tuned mass dampers, and wind tunnel testing"],
        ["Millennium Bridge (2000)","Crowd walking at ~1 Hz excited lateral mode at 1.0 Hz\nPositive feedback: bridge motion encouraged crowd synchronisation\n37 fluid viscous dampers and 52 TMDs retrofitted; reopened 2002"],
        ["Turbine blade resonance","Engine-order excitation: BPF = (number of blades) × RPM/60\nCampbell diagram maps all crossings — sustained operation avoided\nBlades designed with sufficient frequency margin or intentional mistuning"],
        ["Rotating machinery run-up","Must accelerate through ωₙ during startup\nSlow run-through more damaging than fast (more cycles at resonance)\nVariable-frequency drives (VFD) control acceleration rate"],
        ["Seismic isolation","Base isolators (lead-rubber bearings) reduce fₙ to 0.3–0.5 Hz\nEarthquake energy at 1–10 Hz: r = 2–20 → excellent isolation\nFloor accelerations reduced by factor 5–10 vs fixed-base building"],
      ]}/>

      <InfoBox title="Key Formulae Summary" items={[
        "Undamped resonance: x(t) = (F₀/2mωₙ)·t·sin(ωₙt)  [grows linearly — no steady state]",
        "Damped peak:  D_max = 1/(2ζ√(1−ζ²))  at  r* = √(1−2ζ²)",
        "Quality factor:  Q = 1/(2ζ)  ≈  D at r = 1",
        "Half-power bandwidth:  ζ = (ω₂−ω₁)/(2ωₙ) = Δω/(2ωₙ)",
        "Phase at resonance:  φ = 90° for ALL ζ at r = 1  (universal resonance indicator)",
        "Log decrement:  δ = ln(x₁/x₂) = 2πζ/√(1−ζ²)  →  ζ = δ/√(4π²+δ²)",
        "No-peak condition:  ζ ≥ 1/√2 ≈ 0.707  (amplitude decreases monotonically)",
        "Detuning margin:  keep |r−1| > 0.2  (or > 0.1 if ζ > 0.1)",
        "Dynamic absorber tuning:  ωₐ = ωₙ  →  anti-resonance dip at problem frequency",
      ]}/>
    </div>
  );
}